import React from 'react';
import { cn } from '../../lib/utils';

/**
 * ListContainer - Reusable responsive container for list pages
 * Ensures consistent responsive behavior across all list views
 *
 * Features:
 * - Proper viewport constraints (max-w-full, overflow-hidden)
 * - Responsive padding that scales with screen size
 * - Consistent spacing and gaps
 * - Mobile-first approach
 *
 * @param {Object} props
 * @param {ReactNode} props.children - Content to render inside container
 * @param {string} props.className - Additional CSS classes
 */
const ListContainer = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 w-full max-w-full overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * ListContainer.Header - Header section for page title and actions
 */
ListContainer.Header = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * ListContainer.Title - Page title with subtitle
 */
ListContainer.Title = ({ title, subtitle, className }) => {
  return (
    <div className={cn("", className)}>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{title}</h1>
      {subtitle && (
        <p className="mt-2 text-sm sm:text-base text-gray-600">{subtitle}</p>
      )}
    </div>
  );
};

/**
 * ListContainer.Actions - Action buttons in header
 */
ListContainer.Actions = ({ children, className }) => {
  return (
    <div className={cn("flex gap-2 flex-wrap", className)}>
      {children}
    </div>
  );
};

/**
 * ListContainer.Filters - Filter controls section
 * Wraps filter buttons with proper responsive behavior
 */
ListContainer.Filters = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 w-full max-w-full",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * ListContainer.TableView - Desktop table view
 * Hidden on mobile, visible on md+ breakpoint
 */
ListContainer.TableView = ({ children, className }) => {
  return (
    <div
      className={cn(
        "hidden md:block overflow-x-auto",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * ListContainer.CardView - Mobile card view
 * Visible on mobile, hidden on md+ breakpoint
 */
ListContainer.CardView = ({ children, className }) => {
  return (
    <div
      className={cn(
        "md:hidden space-y-3 p-2 w-full max-w-full overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * ListContainer.Content - General content wrapper
 * For content that's visible at all breakpoints
 */
ListContainer.Content = ({ children, className }) => {
  return (
    <div
      className={cn(
        "w-full max-w-full",
        className
      )}
    >
      {children}
    </div>
  );
};

export default ListContainer;
